import { NavigationDropdownNew } from "components/navigationDropdownNew/NavigationDropdownNew";
import { websiteBuilderGenerateDefaultLinkSettings } from "pages/secure/facility/websiteBuilder/editor/nodes/element/websiteBuilderLink";
import { faCircleI, faLink, faParagraph, faPlus } from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import React from "react";
import { useTranslation } from "react-i18next";
import { IWebsiteBuilderMarqueeSettings, TWebsiteBuilderMarqueeItem, TWebsiteBuilderMarqueeItemType } from "pages/secure/facility/websiteBuilder/editor/nodes/container/websiteBuilderMarquee";
import { TWebsiteBuilderHandleUpdateState } from "pages/secure/facility/websiteBuilder/editor/hooks/debouncedUpdate/useWebsiteBuilderDebouncedUpdate";
import { MarqueeSettingsItem } from "./MarqueeSettingsItem";
import { TMarqueeSettingsSubView } from "./MarqueeSettings";
import { websiteBuilderGenerateDefaultIconSettings } from "pages/secure/facility/websiteBuilder/editor/nodes/element/websiteBuilderIcon";

interface IMarqueeSettingsManageItemsProps {
  marqueeId: string;
  items: TWebsiteBuilderMarqueeItem[];
  handleUpdateState: TWebsiteBuilderHandleUpdateState<IWebsiteBuilderMarqueeSettings>;
  setSubView: React.Dispatch<React.SetStateAction<TMarqueeSettingsSubView>>;
}

export function MarqueeSettingsManageItems(props: IMarqueeSettingsManageItemsProps) {
  const { marqueeId, items, handleUpdateState, setSubView } = props;

  const { t } = useTranslation();

  function addItem(type: TWebsiteBuilderMarqueeItemType) {
    const itemId = crypto.randomUUID();

    if (type === "icon") {
      handleUpdateState(prev => ({
        ...prev,
        items: [...prev.items, { id: itemId, type, settings: websiteBuilderGenerateDefaultIconSettings() }],
      }));

      setSubView({ type: "edit-icon", itemId });
    } else if (type === "link") {
      handleUpdateState(prev => ({
        ...prev,
        items: [...prev.items, { id: itemId, type, settings: websiteBuilderGenerateDefaultLinkSettings() }],
      }));

      setSubView({ type: "edit-link", itemId });
    } else if (type === "paragraph") {
      handleUpdateState(prev => ({
        ...prev,
        items: [...prev.items, { id: itemId, type, settings: { text: t("Paragraph"), color: null } }],
      }));

      setSubView({ type: "edit-paragraph", itemId });
    }
  }

  function editItem(item: TWebsiteBuilderMarqueeItem) {
    if (item.type === "icon") {
      setSubView({ type: "edit-icon", itemId: item.id });
    } else if (item.type === "link") {
      setSubView({ type: "edit-link", itemId: item.id });
    } else {
      setSubView({ type: "edit-paragraph", itemId: item.id });
    }
  }

  function moveItem(prevIndex: number, nextIndex: number) {
    if (prevIndex === nextIndex) {
      return;
    }

    handleUpdateState(prev => {
      const items = [...prev.items];

      const temp = items[prevIndex];
      items[prevIndex] = items[nextIndex];
      items[nextIndex] = temp;

      return { ...prev, items };
    });
  }

  function deleteItem(itemId: string) {
    handleUpdateState(prev => ({
      ...prev,
      items: prev.items.filter(item => item.id !== itemId),
    }));
  }

  return (
    <div className="website-builder-marquee-settings-manage-items">
      <div className="marquee-settings-manage-items-header">
        <div className="marquee-settings-manage-items-label">{t("Elements ({{count}})", { count: items.length })}</div>
        <NavigationDropdownNew
          showPlainTextLabel
          hideChevron
          rightAlign
          label={<FontAwesomeIcon icon={faPlus} title={t("Add element")} />}
          sections={[[
            { label: t("Add paragraph"), icon: faParagraph.iconName, onClick: () => addItem("paragraph") },
            { label: t("Add link"), icon: faLink.iconName, onClick: () => addItem("link") },
            { label: t("Add an icon"), icon: faCircleI.iconName, onClick: () => addItem("icon") },
          ]]}
        />
      </div>
      <div className="marquee-settings-manage-items-list">
        {items.map((item, index) => (
          <MarqueeSettingsItem
            key={item.id}
            marqueeId={marqueeId}
            index={index}
            item={item}
            deleteItem={deleteItem}
            moveItem={moveItem}
            onClick={() => editItem(item)}
          />
        ))}
      </div>
    </div>
  );
}